import { useEffect, useState } from "react";
import { api, tokenUrl } from "@/lib/api";
import type { FacilityStatusPayload } from "@/lib/types";

type State = {
  data: FacilityStatusPayload | null;
  state: "idle" | "connecting" | "open" | "closed";
};

/**
 * Server-Protokoll (siehe src/frontend/facility.rs):
 * SSE `data: { markers, counts }` mit Heartbeat-Kommentaren alle 30s.
 * Fallback: einmaliger GET wenn EventSource nicht verfügbar oder mapId leer.
 */
export function useFacilityStatus(mapId: number | null): State {
  const [data, setData] = useState<FacilityStatusPayload | null>(null);
  const [connState, setConnState] = useState<State["state"]>("idle");

  useEffect(() => {
    if (mapId == null) {
      setData(null);
      setConnState("idle");
      return;
    }

    // Erst initialen Snapshot per GET — SSE wartet 1s vor der ersten Nachricht
    let cancelled = false;
    setConnState("connecting");

    void api
      .get<FacilityStatusPayload>(`/api/maps/${mapId}/status`)
      .then((snap) => {
        if (!cancelled) setData(snap);
      })
      .catch(() => undefined);

    const url = tokenUrl(`/api/maps/${mapId}/status/stream`);
    const es = new EventSource(url);
    es.onopen = () => setConnState("open");
    es.onmessage = (e) => {
      try {
        const parsed = JSON.parse(e.data) as FacilityStatusPayload;
        setData(parsed);
      } catch {
        // ignore
      }
    };
    es.onerror = () => setConnState("closed");

    return () => {
      cancelled = true;
      es.close();
    };
  }, [mapId]);

  return { data, state: connState };
}
